import { useState, useEffect } from 'react';
import {
  Building2, MapPin, Clock, UserPlus, Stethoscope,
  Save, Loader2, AlertCircle, CheckCircle, Globe, Phone as PhoneIcon
} from 'lucide-react';
import api from '../api/client';
import PhoneInput from './PhoneInput';
import LocationAutocomplete from './LocationAutocomplete';

// ── Medical specializations ──────────────────────────────────────────────────
const SPECIALIZATIONS = [
  'Allergy & Immunology', 'Anesthesiology', 'Cardiology', 'Dermatology',
  'Emergency Medicine', 'Endocrinology', 'Family Medicine', 'Gastroenterology',
  'General Practice', 'General Surgery', 'Geriatrics', 'Hematology',
  'Infectious Disease', 'Internal Medicine', 'Medical Genetics', 'Neonatology',
  'Nephrology', 'Neurology', 'Neurosurgery', 'Obstetrics & Gynecology',
  'Oncology', 'Ophthalmology', 'Orthopedic Surgery', 'Otolaryngology (ENT)',
  'Pathology', 'Pediatrics', 'Physical Medicine & Rehabilitation',
  'Plastic Surgery', 'Psychiatry', 'Pulmonology', 'Radiation Oncology',
  'Radiology', 'Rheumatology', 'Sports Medicine', 'Thoracic Surgery',
  'Urology', 'Vascular Surgery', 'Other',
];

// ── Schedule builder helpers ─────────────────────────────────────────────────
const DAYS = [
  { key: 'mon', label: 'Mon' },
  { key: 'tue', label: 'Tue' },
  { key: 'wed', label: 'Wed' },
  { key: 'thu', label: 'Thu' },
  { key: 'fri', label: 'Fri' },
  { key: 'sat', label: 'Sat' },
  { key: 'sun', label: 'Sun' },
];

// Half-hour time slots, 6 AM – 10 PM
const TIME_SLOTS = [];
for (let h = 6; h <= 22; h++) {
  for (const m of [0, 30]) {
    if (h === 22 && m === 30) break;
    const period  = h < 12 ? 'AM' : 'PM';
    const displayH = h === 0 ? 12 : h > 12 ? h - 12 : h;
    const displayM = m === 0 ? '00' : '30';
    TIME_SLOTS.push(`${displayH}:${displayM} ${period}`);
  }
}

const buildScheduleString = (days, from, to) => {
  const active = DAYS.filter(d => days[d.key]).map(d => d.label);
  if (active.length === 0) return '';
  const dayStr =
    active.length === 7 ? 'Every day'
    : active.join(', ') === 'Mon, Tue, Wed, Thu, Fri' ? 'Mon – Fri'
    : active.join(', ');
  return `${dayStr}, ${from} – ${to}`;
};

// ── Shared field classes ─────────────────────────────────────────────────────
const FIELD = 'w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm text-slate-900 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-600 transition-all';
const LABEL = 'block text-xs font-bold text-slate-600 uppercase tracking-widest mb-2';

const DoctorProfileSettings = () => {
  const [profile, setProfile] = useState({
    specialization: '',
    bio: '',
    phone: '',
    institution: '',
    location: { city: '', state: '', zipCode: '', country: 'USA', timezone: 'America/New_York' },
    availability: { acceptingNewPatients: true, schedule: '' },
  });

  // Structured schedule state (separate from the stored string)
  const [scheduleDays, setScheduleDays] = useState(
    { mon: true, tue: true, wed: true, thu: true, fri: true, sat: false, sun: false }
  );
  const [scheduleFrom, setScheduleFrom] = useState('9:00 AM');
  const [scheduleTo,   setScheduleTo]   = useState('5:00 PM');

  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => { loadProfile(); }, []);

  const loadProfile = async () => {
    try {
      const response = await api.get('/profile');
      const user = response.data?.user || {};
      setProfile({
        specialization: user.specialization || '',
        bio:            user.bio            || '',
        phone:          user.phone          || '',
        institution:    user.institution    || '',
        location:       user.location       || { city: '', state: '', zipCode: '', country: 'USA', timezone: 'America/New_York' },
        availability:   user.availability   || { acceptingNewPatients: true, schedule: '' },
      });
    } catch (err) {
      setError('Failed to load profile information');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(false);

    // Derive the schedule string from the structured builder
    const scheduleStr = buildScheduleString(scheduleDays, scheduleFrom, scheduleTo);

    try {
      await api.put('/profile', {
        specialization: profile.specialization,
        institution:    profile.institution,
        location:       profile.location,
        bio:            profile.bio,
        phone:          profile.phone,
        availability: {
          ...profile.availability,
          schedule: scheduleStr,
        },
      });
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError('Failed to save changes. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleLocationChange = (addressData) => {
    setProfile(prev => ({ ...prev, location: { ...prev.location, ...addressData } }));
  };

  const toggleDay = (key) => {
    setScheduleDays(prev => ({ ...prev, [key]: !prev[key] }));
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-slate-100 rounded-lg w-1/4" />
          <div className="h-32 bg-slate-100 rounded-lg" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
      {/* Header */}
      <div className="px-6 py-5 border-b border-slate-100">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-blue-50 flex items-center justify-center">
            <Stethoscope className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h2 className="font-headline font-bold text-slate-900">Practice Settings</h2>
            <p className="text-xs text-slate-500 mt-0.5">Availability, location and contact details</p>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-8">

        {/* ── Institution ───────────────────────────────────────── */}
        <div>
          <label className={LABEL}>Institution / Practice Name</label>
          <div className="relative">
            <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              value={profile.institution}
              onChange={(e) => setProfile(p => ({ ...p, institution: e.target.value }))}
              placeholder="e.g., Massachusetts General Hospital"
              className={`${FIELD} pl-10`}
            />
          </div>
          <p className="text-xs text-slate-400 mt-1.5">Shown to patients when browsing available doctors</p>
        </div>

        {/* ── Practice Location ─────────────────────────────────── */}
        <div className="border-t border-slate-100 pt-6">
          <div className="flex items-center gap-2 mb-4">
            <MapPin className="w-4 h-4 text-blue-600" />
            <h3 className={`${LABEL} mb-0`}>Practice Location</h3>
          </div>

          <LocationAutocomplete
            label="Address"
            value={profile.location}
            onChange={handleLocationChange}
            placeholder="Search for your practice address…"
          />

          <div className="grid grid-cols-2 gap-3 mt-3">
            <div>
              <label className={LABEL}>City</label>
              <input
                type="text"
                value={profile.location.city}
                onChange={(e) => handleLocationChange({ ...profile.location, city: e.target.value })}
                placeholder="City"
                className={FIELD}
              />
            </div>
            <div>
              <label className={LABEL}>State / Province</label>
              <input
                type="text"
                value={profile.location.state}
                onChange={(e) => handleLocationChange({ ...profile.location, state: e.target.value })}
                placeholder="State"
                className={FIELD}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 mt-3">
            <div>
              <label className={LABEL}>ZIP / Postal Code</label>
              <input
                type="text"
                value={profile.location.zipCode}
                onChange={(e) => handleLocationChange({ ...profile.location, zipCode: e.target.value })}
                placeholder="ZIP"
                className={FIELD}
              />
            </div>
            <div>
              <label className={LABEL}>Timezone</label>
              <div className="relative">
                <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                <select
                  value={profile.location.timezone}
                  onChange={(e) => handleLocationChange({ ...profile.location, timezone: e.target.value })}
                  className={`${FIELD} pl-9 appearance-none`}
                >
                  <option value="America/New_York">Eastern (ET)</option>
                  <option value="America/Chicago">Central (CT)</option>
                  <option value="America/Denver">Mountain (MT)</option>
                  <option value="America/Los_Angeles">Pacific (PT)</option>
                  <option value="America/Anchorage">Alaska (AKT)</option>
                  <option value="Pacific/Honolulu">Hawaii (HT)</option>
                  <option value="Europe/London">London (GMT)</option>
                  <option value="Europe/Paris">Paris (CET)</option>
                  <option value="Asia/Dubai">Dubai (GST)</option>
                  <option value="Asia/Kolkata">India (IST)</option>
                  <option value="Asia/Tokyo">Tokyo (JST)</option>
                  <option value="Australia/Sydney">Sydney (AEST)</option>
                  <option value="Africa/Lagos">Lagos (WAT)</option>
                  <option value="Africa/Nairobi">Nairobi (EAT)</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* ── Availability ──────────────────────────────────────── */}
        <div className="border-t border-slate-100 pt-6">
          <div className="flex items-center gap-2 mb-4">
            <Clock className="w-4 h-4 text-blue-600" />
            <h3 className={`${LABEL} mb-0`}>Availability</h3>
          </div>

          {/* Accepting new patients toggle */}
          <div className={`flex items-center justify-between p-4 rounded-xl mb-5 ${
            profile.availability.acceptingNewPatients ? 'bg-emerald-50 border border-emerald-100' : 'bg-slate-50 border border-slate-100'
          }`}>
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${profile.availability.acceptingNewPatients ? 'bg-emerald-100' : 'bg-slate-200'}`}>
                <UserPlus className={`w-4 h-4 ${profile.availability.acceptingNewPatients ? 'text-emerald-600' : 'text-slate-500'}`} />
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-900">Accepting New Patients</p>
                <p className="text-xs text-slate-500 mt-0.5">
                  {profile.availability.acceptingNewPatients
                    ? 'Patients can be assigned to you automatically'
                    : 'You will not receive new patient assignments'}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setProfile(prev => ({
                ...prev,
                availability: { ...prev.availability, acceptingNewPatients: !prev.availability.acceptingNewPatients }
              }))}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/30 ${
                profile.availability.acceptingNewPatients ? 'bg-blue-600' : 'bg-slate-300'
              }`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                profile.availability.acceptingNewPatients ? 'translate-x-6' : 'translate-x-1'
              }`} />
            </button>
          </div>

          {/* Schedule builder */}
          <div>
            <label className={LABEL}>Typical Schedule</label>

            {/* Day toggles */}
            <div className="flex gap-1.5 mb-3">
              {DAYS.map(({ key, label }) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => toggleDay(key)}
                  className={`flex-1 py-2 rounded-lg text-xs font-bold transition-all focus:outline-none focus:ring-2 focus:ring-blue-500/30 ${
                    scheduleDays[key]
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            {/* Time range */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-slate-500 font-medium mb-1.5 block">From</label>
                <select
                  value={scheduleFrom}
                  onChange={(e) => setScheduleFrom(e.target.value)}
                  className={`${FIELD} appearance-none`}
                >
                  {TIME_SLOTS.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-slate-500 font-medium mb-1.5 block">To</label>
                <select
                  value={scheduleTo}
                  onChange={(e) => setScheduleTo(e.target.value)}
                  className={`${FIELD} appearance-none`}
                >
                  {TIME_SLOTS.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            </div>

            {/* Preview */}
            {buildScheduleString(scheduleDays, scheduleFrom, scheduleTo) && (
              <p className="text-xs text-slate-500 mt-2">
                <span className="font-semibold text-slate-700">Preview: </span>
                {buildScheduleString(scheduleDays, scheduleFrom, scheduleTo)}
              </p>
            )}
          </div>
        </div>

        {/* ── Contact Information ───────────────────────────────── */}
        <div className="border-t border-slate-100 pt-6">
          <h3 className={`${LABEL} mb-4`}>Contact Information</h3>

          <div className="space-y-4">
            {/* Phone with country code */}
            <div>
              <label className={LABEL}>
                <span className="flex items-center gap-1.5"><PhoneIcon className="w-3 h-3" /> Phone</span>
              </label>
              <PhoneInput
                value={profile.phone}
                onChange={(val) => setProfile(p => ({ ...p, phone: val }))}
              />
            </div>

            {/* Specialization dropdown */}
            <div>
              <label className={LABEL}>Specialization</label>
              <div className="relative">
                <Stethoscope className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                <select
                  value={profile.specialization}
                  onChange={(e) => setProfile(p => ({ ...p, specialization: e.target.value }))}
                  className={`${FIELD} pl-9 appearance-none`}
                >
                  <option value="">Select your specialization…</option>
                  {SPECIALIZATIONS.map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Bio */}
            <div>
              <label className={LABEL}>Bio</label>
              <textarea
                value={profile.bio}
                onChange={(e) => setProfile(p => ({ ...p, bio: e.target.value }))}
                placeholder="Brief description of your practice and expertise…"
                rows={3}
                className={`${FIELD} resize-none`}
              />
            </div>
          </div>
        </div>

        {/* ── Feedback & Save ───────────────────────────────────── */}
        {error && (
          <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-100 rounded-xl">
            <AlertCircle className="w-4 h-4 text-red-500 shrink-0" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}
        {success && (
          <div className="flex items-center gap-3 p-4 bg-emerald-50 border border-emerald-100 rounded-xl">
            <CheckCircle className="w-4 h-4 text-emerald-500 shrink-0" />
            <p className="text-sm text-emerald-700">Settings saved successfully!</p>
          </div>
        )}

        <div className="flex justify-end pt-2 border-t border-slate-100">
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-blue-700 to-blue-600 text-white rounded-xl text-sm font-bold shadow-sm hover:from-blue-800 hover:to-blue-700 hover:shadow-md transition-all disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {saving
              ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</>
              : <><Save className="w-4 h-4" /> Save Changes</>}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DoctorProfileSettings;
